import { Inject, Injectable, Optional } from '@angular/core';
import { MAT_DATE_LOCALE, NativeDateAdapter } from '@angular/material/core';
import { Platform } from '@angular/cdk/platform';
import { DateFormatService } from 'src/app/core/services/date-format.service';
import { DateFormatPref } from 'src/app/core/services/date-format.util';
import { formatDatePreset, isNumericDateInput } from './date-preset-format';

const FORMAT_LOCALE_FALLBACK = 'it-IT';

function browserFormatLocale(): string {
  return (typeof navigator !== 'undefined' && navigator.language) || FORMAT_LOCALE_FALLBACK;
}

/** Whether a typed string is shaped like the given preset (right separator, three
 *  all-digit parts). Distinguishes "this is the user's format" (parse it, reject if
 *  invalid) from "some other format" (defer to the default parser). */
function matchesPresetShape(value: string, pref: Exclude<DateFormatPref, 'system'>): boolean {
  const sep = pref === 'yyyy-MM-dd' ? '-' : '/';
  const parts = value.split(sep).map(p => p.trim());
  return parts.length === 3 && parts.every(p => /^\d+$/.test(p));
}

/** Parse a typed, preset-shaped string into a local Date (matching formatWithPref's
 *  local getters, so format/parse round-trip cleanly). Returns null for an impossible
 *  date (e.g. 31/02). Assumes the caller has confirmed the shape via matchesPresetShape. */
function parseWithPref(value: string, pref: Exclude<DateFormatPref, 'system'>): Date | null {
  const sep = pref === 'yyyy-MM-dd' ? '-' : '/';
  const parts = value.split(sep).map(p => p.trim());
  const nums = parts.map(p => parseInt(p, 10));
  let yyyy: number;
  let mm: number;
  let dd: number;
  if (pref === 'dd/MM/yyyy') {
    dd = nums[0];
    mm = nums[1];
    yyyy = nums[2];
  } else if (pref === 'MM/dd/yyyy') {
    mm = nums[0];
    dd = nums[1];
    yyyy = nums[2];
  } else {
    yyyy = nums[0];
    mm = nums[1];
    dd = nums[2];
  }
  const date = new Date(yyyy, mm - 1, dd);
  // Reject overflow (e.g. 31/02 rolling into March) by requiring a round-trip.
  if (date.getFullYear() !== yyyy || date.getMonth() !== mm - 1 || date.getDate() !== dd) {
    return null;
  }
  return date;
}

@Injectable()
export class FixedFormatDateAdapter extends NativeDateAdapter {
  constructor(
    @Optional() @Inject(MAT_DATE_LOCALE) matDateLocale: string,
    platform: Platform,
    private dateFormatService: DateFormatService,
  ) {
    super(matDateLocale, platform);
  }

  override format(date: Date, displayFormat: Object): string {
    if (!this.isValid(date)) {
      throw Error('FixedFormatDateAdapter: Cannot format invalid date.');
    }
    const opts = displayFormat as Intl.DateTimeFormatOptions;
    // The numeric date-input field (day present, numeric month) honours the user's
    // chosen date format. Textual month labels (the calendar header period button)
    // follow the UI language via this.locale (kept in sync by AppLocaleService);
    // numeric formatting stays on the browser locale so dd/MM ordering doesn't shift.
    const pref = this.dateFormatService.currentDateFormat;
    if (pref !== 'system' && isNumericDateInput(opts)) {
      return formatDatePreset(date, pref);
    }
    const textualMonth = opts.month === 'short' || opts.month === 'long' || opts.month === 'narrow';
    const locale = textualMonth ? (this.locale || FORMAT_LOCALE_FALLBACK) : browserFormatLocale();
    const dtf = new Intl.DateTimeFormat(locale, {
      ...opts,
      timeZone: 'utc',
    });
    const d = new Date();
    d.setUTCFullYear(date.getFullYear(), date.getMonth(), date.getDate());
    d.setUTCHours(date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds());
    return dtf.format(d);
  }

  override getFirstDayOfWeek(): number {
    return this.dateFormatService.currentFirstDayOfWeek;
  }

  override parse(value: any, parseFormat?: any): Date | null {
    // When the user picked a concrete format, parse typed input by that exact pattern
    // (so a day-first "31/12/2026" is read correctly and round-trips with format()).
    // Anything that doesn't match falls through to the default parser, so inputs that
    // worked before keep working -- this is purely additive.
    const pref = this.dateFormatService.currentDateFormat;
    if (pref !== 'system' && typeof value === 'string') {
      const trimmed = value.trim();
      // Input is in the user's chosen format: honour it, and reject an invalid date
      // (null) rather than handing it to the lenient default parser. Inputs that
      // aren't shaped like the preset still defer to the default parser below.
      if (matchesPresetShape(trimmed, pref)) {
        return parseWithPref(trimmed, pref);
      }
    }
    return super.parse(value, parseFormat);
  }
}
